/**
 * MediaDiscIcons - Custom disc icons for CD, DVD, and Blu-ray
 * Provides consistent disc-shaped icons with appropriate color schemes
 */
import React from 'react';

/**
 * CD Disc Icon - Silver/grey disc
 */
export const CDIcon = ({ size = 20, className = '' }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        className={className}
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="cdGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E8E8E8" />
                <stop offset="30%" stopColor="#C0C0C0" />
                <stop offset="50%" stopColor="#A8A8A8" />
                <stop offset="70%" stopColor="#C0C0C0" />
                <stop offset="100%" stopColor="#D8D8D8" />
            </linearGradient>
            <linearGradient id="cdShine" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
                <stop offset="50%" stopColor="rgba(255,255,255,0)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.3)" />
            </linearGradient>
        </defs>
        {/* Outer disc */}
        <circle cx="12" cy="12" r="10" fill="url(#cdGradient)" stroke="#A0A0A0" strokeWidth="0.5" />
        {/* Shine effect */}
        <circle cx="12" cy="12" r="10" fill="url(#cdShine)" />
        {/* Inner ring */}
        <circle cx="12" cy="12" r="6" fill="none" stroke="#888888" strokeWidth="0.3" opacity="0.5" />
        {/* Center hole */}
        <circle cx="12" cy="12" r="2.5" fill="#F8F8F8" stroke="#909090" strokeWidth="0.5" />
        {/* Center hole inner */}
        <circle cx="12" cy="12" r="1.5" fill="#FFFFFF" />
    </svg>
);

/**
 * DVD Disc Icon - Gold/amber disc
 */
export const DVDIcon = ({ size = 20, className = '' }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        className={className}
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="dvdGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F5E6A3" />
                <stop offset="30%" stopColor="#DAA520" />
                <stop offset="50%" stopColor="#B8860B" />
                <stop offset="70%" stopColor="#DAA520" />
                <stop offset="100%" stopColor="#F0D890" />
            </linearGradient>
            <linearGradient id="dvdShine" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
                <stop offset="50%" stopColor="rgba(255,255,255,0)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.25)" />
            </linearGradient>
        </defs>
        {/* Outer disc */}
        <circle cx="12" cy="12" r="10" fill="url(#dvdGradient)" stroke="#B8860B" strokeWidth="0.5" />
        {/* Shine effect */}
        <circle cx="12" cy="12" r="10" fill="url(#dvdShine)" />
        {/* Inner ring */}
        <circle cx="12" cy="12" r="6" fill="none" stroke="#DAA520" strokeWidth="0.3" opacity="0.5" />
        {/* Center hole */}
        <circle cx="12" cy="12" r="2.5" fill="#FFF8E7" stroke="#B8860B" strokeWidth="0.5" />
        {/* Center hole inner */}
        <circle cx="12" cy="12" r="1.5" fill="#FFFFFF" />
    </svg>
);

/**
 * Blu-ray Disc Icon - Blue disc with same visual style as CD/DVD
 */
export const BlurayIcon = ({ size = 20, className = '' }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        className={className}
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="blurayGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#93C5FD" />
                <stop offset="30%" stopColor="#3B82F6" />
                <stop offset="50%" stopColor="#1D4ED8" />
                <stop offset="70%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#60A5FA" />
            </linearGradient>
            <linearGradient id="blurayShine" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
                <stop offset="50%" stopColor="rgba(255,255,255,0)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.25)" />
            </linearGradient>
        </defs>
        {/* Outer disc */}
        <circle cx="12" cy="12" r="10" fill="url(#blurayGradient)" stroke="#1D4ED8" strokeWidth="0.5" />
        {/* Shine effect */}
        <circle cx="12" cy="12" r="10" fill="url(#blurayShine)" />
        {/* Inner ring */}
        <circle cx="12" cy="12" r="6" fill="none" stroke="#60A5FA" strokeWidth="0.3" opacity="0.5" />
        {/* Center hole */}
        <circle cx="12" cy="12" r="2.5" fill="#EFF6FF" stroke="#3B82F6" strokeWidth="0.5" />
        {/* Center hole inner */}
        <circle cx="12" cy="12" r="1.5" fill="#FFFFFF" />
    </svg>
);

/**
 * Generic function to get the appropriate disc icon by type
 */
export const getMediaDiscIcon = (type, size = 20, className = '') => {
    switch (type) {
        case 'cd':
            return <CDIcon size={size} className={className} />;
        case 'dvd':
            return <DVDIcon size={size} className={className} />;
        case 'bluray':
            return <BlurayIcon size={size} className={className} />;
        default:
            return null;
    }
};

/**
 * MediaDiscIcon component - renders the appropriate disc based on type prop
 */
export const MediaDiscIcon = ({ type, size = 20, className = '' }) => {
    return getMediaDiscIcon(type, size, className);
};

export default MediaDiscIcon;
